import React, { memo, useCallback, useContext, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { createUseStyles, useTheme } from '@Sage/hoi-poi-ui';

import { CSSTransition } from 'react-transition-group';

import { SidebarContext, SidebarProvider, SidebarConsumer } from './components/SidebarContext';
import LogoSidebar from './components/LogoSidebar';
import ToggleSidebar from './components/ToggleSidebar';
import MenuItem from './components/MenuItem';
import MenuSubmenu from './components/MenuSubmenu';
import MenuTooltip from './components/MenuTooltip';
import MultiEnvironmentSection from './components/MultiEnvironmentSection';

import styles from './styles/index';
const useStyles = createUseStyles(styles, 'Sidebar');

const Sidebar = memo(
    ({
        className: classNameProp,
        currentHref = '',
        customColor,
        customLogo,
        defaultEnvironment,
        delayHover = 800,
        delayTooltip = 800,
        environments = [],
        environmentsSelected = [],
        getPostComponent,
        onEnvironmentsChange,
        onExpand,
        onMatchActive,
        schema,
        withMultiEnvironmentSection = false,
    }) => {
        const { appName, homeLink, menuItems } = schema;
        const { isExpanded, isMobile, setExpanded } = useContext(SidebarContext);
        const [hover, setHover] = useState(false);
        const [showTooltip, setShowTooltip] = useState(false);
        const [tooltip, setTooltip] = useState({});
        const tooltipTimeoutRef = useRef(null);
        const hoverTimeoutRef = useRef(null);
        const tooltipNodeRef = useRef(null);
        const theme = useTheme();

        const classes = useStyles();
        const rootClassName = classnames(
            classes.root,
            {
                [classes.isExpanded]: isExpanded,
                [classes.isHover]: hover || isMobile,
            },
            classNameProp,
        );

        const handleClearTooltip = useCallback(() => {
            tooltipTimeoutRef.current = setTimeout(() => {
                setShowTooltip(false);
            }, delayTooltip);
        }, [delayTooltip]);

        const handleShowTooltip = useCallback((data) => {
            clearTimeout(tooltipTimeoutRef.current);
            setTooltip(data);
            setShowTooltip(true);
        }, []);

        const handleHover = useCallback(
            (hover = true) => {
                clearTimeout(hoverTimeoutRef.current);
                const timeout = hover ? 0 : delayHover;
                hoverTimeoutRef.current = setTimeout(() => {
                    setHover(hover);
                    if (!hover) setShowTooltip(false);
                }, timeout);
            },
            [delayHover, setHover],
        );

        const handleToggle = useCallback(() => {
            if (onExpand) onExpand();
            setExpanded((isExpanded) => !isExpanded);
        }, [setExpanded, onExpand]);

        const getSubmenuActive = useCallback(
            (item) => {
                if (onMatchActive) return onMatchActive(item);
                return (
                    item.submenuItems?.some(
                        (sub) =>
                            currentHref.includes(sub.link) ||
                            sub.link === `..${window.location.pathname}`,
                    ) || false
                );
            },
            [currentHref, onMatchActive],
        );

        const getMenuItem = useCallback(
            (type, itemProps, key) =>
                ({
                    divider: <span key={key} className={classes.divider}></span>,
                    link: <MenuItem key={key} {...itemProps} />,
                    submenu: <MenuSubmenu key={key} {...itemProps} />,
                })[type],
            [classes.divider],
        );

        const getMenuItems = useMemo(() => {
            if (!withMultiEnvironmentSection) {
                return menuItems.map((item, index) => {
                    const { type, ...otherProps } = item;

                    const selected =
                        type === 'submenu'
                            ? getSubmenuActive(item)
                            : onMatchActive
                              ? onMatchActive(item)
                              : currentHref.includes(item.link) ||
                                item.link === `..${window.location.pathname}`;

                    const itemProps = {
                        isActive: selected || false,
                        currentHref,
                        onMatchActive,
                        ...(type === 'submenu'
                            ? {}
                            : {
                                  onMouseEnter: handleShowTooltip,
                                  onMouseLeave: handleClearTooltip,
                              }),
                        ...otherProps,
                    };

                    return getMenuItem(type, itemProps, `item-${item.link || item.name || index}`);
                });
            }

            const multiEnvItems = [];
            const regularItems = [];
            let firstMultiEnvIndex = -1;

            menuItems.forEach((item) => {
                if (item.isMultiEnvironment) {
                    if (firstMultiEnvIndex === -1) {
                        firstMultiEnvIndex = regularItems.length;
                    }
                    multiEnvItems.push(item);
                } else {
                    regularItems.push(item);
                }
            });

            const renderedItems = regularItems.map((item, index) => {
                const { type, ...otherProps } = item;

                const selected =
                    type === 'submenu'
                        ? getSubmenuActive(item)
                        : onMatchActive
                          ? onMatchActive(item)
                          : currentHref.includes(item.link) ||
                            item.link === `..${window.location.pathname}`;

                const itemProps = {
                    isActive: selected || false,
                    currentHref,
                    onMatchActive,
                    ...(type === 'submenu'
                        ? {}
                        : {
                              onMouseEnter: handleShowTooltip,
                              onMouseLeave: handleClearTooltip,
                          }),
                    ...otherProps,
                };

                return getMenuItem(type, itemProps, `item-${item.link || item.name || index}`);
            });

            if (multiEnvItems.length > 0 && firstMultiEnvIndex >= 0) {
                renderedItems.splice(
                    firstMultiEnvIndex,
                    0,
                    <MultiEnvironmentSection
                        key="multi-environment-section"
                        currentHref={currentHref}
                        defaultEnvironment={defaultEnvironment}
                        environments={environments}
                        environmentsSelected={environmentsSelected}
                        items={multiEnvItems}
                        onEnvironmentsChange={onEnvironmentsChange}
                        onMatchActive={onMatchActive}
                        onMouseEnter={handleShowTooltip}
                        onMouseLeave={handleClearTooltip}
                    />,
                );
            }

            return renderedItems;
        }, [
            currentHref,
            defaultEnvironment,
            environments,
            environmentsSelected,
            getMenuItem,
            getSubmenuActive,
            handleClearTooltip,
            handleShowTooltip,
            menuItems,
            onEnvironmentsChange,
            onMatchActive,
            withMultiEnvironmentSection,
        ]);

        const finalColor =
            customColor && customColor !== '' ? customColor : theme.colors.primary.black;

        return (
            <nav
                className={rootClassName}
                onMouseEnter={(e) => handleHover()}
                onMouseLeave={(e) => handleHover(false)}
            >
                <div style={{ backgroundColor: finalColor }} className={classes.inner}>
                    <div className={classes.brand}>
                        <LogoSidebar
                            appName={appName}
                            homeLink={homeLink}
                            isExpanded={isExpanded}
                            customLogo={customLogo}
                        />
                    </div>
                    {!getPostComponent && (
                        <div className={classes.menu}>
                            <div className={classes.menuInner}>{getMenuItems}</div>
                        </div>
                    )}
                    {getPostComponent && (
                        <div className={classes.menuContainer}>
                            <div className={classes.menu}>
                                <div className={classes.menuInner}>{getMenuItems}</div>
                            </div>
                            <div className={classes.postComponent}>
                                {getPostComponent({ isExpanded })}
                            </div>
                        </div>
                    )}
                </div>
                <ToggleSidebar
                    className={classes.toggleSidebar}
                    isExpanded={isExpanded}
                    isVisible={hover || isMobile}
                    onClick={handleToggle}
                    customColor={customColor}
                />
                {!isExpanded && !isMobile && (
                    <CSSTransition
                        classNames="tooltip-transition"
                        in={showTooltip}
                        nodeRef={tooltipNodeRef}
                        timeout={400}
                        unmountOnExit
                    >
                        <MenuTooltip
                            ref={tooltipNodeRef}
                            name={tooltip?.name}
                            top={tooltip?.elTop}
                            className={classnames(classes.menuTooltip, classes.tooltipTransition)}
                        />
                    </CSSTransition>
                )}
            </nav>
        );
    },
);

Sidebar.propTypes = {
    className: PropTypes.string,
    currentHref: PropTypes.string,
    defaultEnvironment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    delayHover: PropTypes.number,
    delayTooltip: PropTypes.number,
    environments: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    ),
    environmentsSelected: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    ),
    onEnvironmentsChange: PropTypes.func,
    onMatchActive: PropTypes.func,
    schema: PropTypes.shape({
        appName: PropTypes.string,
        homeLink: PropTypes.string,
        menuItems: PropTypes.arrayOf(
            PropTypes.oneOfType([
                PropTypes.shape({
                    type: PropTypes.oneOf(['divider']),
                }),
                PropTypes.shape({
                    type: PropTypes.oneOf(['link']),
                    icon: PropTypes.string,
                    isMultiEnvironment: PropTypes.bool,
                    isNew: PropTypes.bool,
                    isNewTab: PropTypes.bool,
                    link: PropTypes.string,
                    name: PropTypes.string,
                }),
                PropTypes.shape({
                    type: PropTypes.oneOf(['submenu']),
                    icon: PropTypes.string,
                    name: PropTypes.string,
                    submenuItems: PropTypes.arrayOf(
                        PropTypes.shape({
                            icon: PropTypes.string,
                            link: PropTypes.string,
                            name: PropTypes.string,
                        }),
                    ),
                }),
            ]),
        ),
    }).isRequired,
    withMultiEnvironmentSection: PropTypes.bool,
};

export default Sidebar;

export { SidebarContext, SidebarProvider, SidebarConsumer, ToggleSidebar };
